"use client";

import React, { useEffect } from "react";
import { Heart, ShieldCheck, Globe, DollarSign, PawPrint } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
export default function Donate() {
  return <section data-section-id="18035" id="founderdonors" className="bg-background py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-30">
        <div className="absolute -top-[10%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-[20%] right-[5%] w-[30%] h-[30%] rounded-full bg-secondary/20 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {}
          <div className="flex flex-col justify-center h-full space-y-8 pt-4 lg:pt-12">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/50 text-primary text-sm font-medium w-fit border border-ring/20">
                <Heart className="w-4 h-4 fill-current" />
                <span>Support Our Haven</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif text-primary leading-[1.1]">
                Every story shared is a pathway to resilience.
              </h2>
              <p className="text-lg text-muted-foreground max-w-xl leading-relaxed">
                The Story Haven provides children and caregivers a safe, guided space to express their life experiences through storytelling and art. Your gift funds workshops that build emotional agency and healing for families.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[{
              icon: ShieldCheck,
              title: "Secure Giving",
              desc: "Your contribution is processed with the highest security and transparency."
            }, {
              icon: Globe,
              title: "Community Impact",
              desc: "We empower families across the region to find hope through creative expression."
            }].map((item, i) => <div key={i} data-index={i} className="flex gap-4 items-start">
                  <div className="p-3 rounded-xl bg-card shadow-sm border border-border/50 text-ring shrink-0">
                    <item.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-primary mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>)}
            </div>

            <div className="pt-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="w-10 h-10 rounded-full border-2 border-background bg-muted flex items-center justify-center overflow-hidden">
                      <img src={`/img/donor_avatar_diverse_${i + 1}.png`} alt="Supporting Donor" className="w-full h-full object-cover" />
                    </div>)}
                </div>
                <p>Join <span className="font-semibold text-primary">450+</span> partners this season</p>
              </div>
            </div>
          </div>

          {}
          <div className="relative">
            <div className="absolute inset-0 bg-secondary/20 transform rotate-2 rounded-3xl blur-sm -z-10 scale-[0.98] translate-y-4" />
            
            <Card className="border-none shadow-2xl bg-card/80 backdrop-blur-md overflow-hidden rounded-[2.5rem]">
              <div className="bg-primary/5 px-6 py-4 border-b border-border/50">
                <div className="flex justify-between items-end mb-2">
                  <span className="text-sm font-medium text-primary">Annual Goal</span>
                  <span className="text-sm font-bold text-foreground">0% Reached</span>
                </div>
                <Progress value={62} className="h-2 bg-primary/10" />
                <p className="text-xs text-muted-foreground mt-2 text-right">
                  $31,000 raised of $50,000 goal
                </p>
              </div>

              <CardHeader className="pb-2">
                <CardTitle className="text-2xl font-serif text-primary">Become a Founder Donor</CardTitle>
                <CardDescription className="text-muted-foreground">
                  Select a gift level to support our sanctuary for stories.
                </CardDescription>
              </CardHeader>

              <CardContent>
                <FormProvider formId="donate_form_home" sectionName="donate_section" defaultValues={{
                donor_name: "",
                donor_email: "",
                donor_phone: "",
                donation_amount: "50",
                recurring: false,
                payment_method: "Credit Card",
                donor_message: ""
              }} validationRules={{
                donor_name: {
                  required: true
                },
                donor_email: {
                  required: true,
                  pattern: "email"
                },
                donation_amount: {
                  required: true,
                  min: 1
                },
                payment_method: {
                  required: true
                }
              }} submitText="Donate Now" successMessage="Thank you for your generous gift! Your support helps children tell their stories with courage.">
                  <DonateForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}
function DonateForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control,
    setValue,
    watch
  } = useWvcForm();
  const currentAmount = watch("donation_amount");
  const handlePresetChange = (value: string) => {
    if (value) {
      setValue("donation_amount", value, {
        shouldValidate: true
      });
    }
  };
  if (isSubmitted && !successMessage.includes("Failed")) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-accent text-ring rounded-full flex items-center justify-center mb-2">
          <PawPrint className="w-8 h-8" />
        </div>
        <h3 className="text-2xl font-serif font-bold text-primary">Thank You!</h3>
        <p className="text-muted-foreground max-w-xs mx-auto">
          Your generosity empowers children to find their voice and heal through creative expression.
        </p>
        <Button variant="outline" className="mt-4 border-ring text-primary hover:bg-accent" onClick={() => window.location.reload()}>
          Make Another Gift
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      {}
      <div className="space-y-3">
        <FormLabel className="text-primary font-medium">Select Amount</FormLabel>
        <ToggleGroup type="single" value={["25", "50", "100"].includes(currentAmount) ? currentAmount : ""} onValueChange={handlePresetChange} className="justify-start w-full gap-2">
          {["25", "50", "100"].map((amount, i) => <ToggleGroupItem key={amount} data-index={i} value={amount} className="flex-1 border border-input data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary hover:bg-accent hover:text-primary transition-all py-3 h-auto text-base font-medium rounded-xl">
              ${amount}
            </ToggleGroupItem>)}
        </ToggleGroup>

        <FormField control={control} name="donation_amount" render={({
        field
      }) => <FormItem>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">$</span>
                <FormControl>
                  <Input type="number" placeholder="Other Amount" className="pl-7 h-12 text-lg font-medium bg-background border-input rounded-xl" {...field} />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="recurring" render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-[1.5rem] border p-4 bg-secondary/30">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="h-4 w-4 mt-1 rounded border-input text-primary focus:ring-ring accent-primary cursor-pointer" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="cursor-pointer font-medium text-primary">
                Make this a monthly recurring gift
              </FormLabel>
              <FormDescription className="text-xs">
                Sustained support helps us plan our therapeutic programs.
              </FormDescription>
            </div>
          </FormItem>} />

      <Separator className="my-4 opacity-50" />

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="donor_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="Evelyn Thorne" {...field} className="bg-background rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="donor_email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="evelyn@example.com" {...field} className="bg-background rounded-xl" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="donor_phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary">Phone Number <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 555-000-0000" {...field} className="bg-background rounded-xl" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="payment_method" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary">Payment Method</FormLabel>
            <FormControl>
              <div className="relative">
                <select className="flex h-10 w-full items-center justify-between rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 appearance-none" {...field}>
                  <option value="Credit Card">Credit Card</option>
                  <option value="PayPal">PayPal</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
                  <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="donor_message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-primary">Message <span className="text-muted-foreground font-normal">(Optional)</span></FormLabel>
            <FormControl>
              <textarea className="flex min-h-[80px] w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y" placeholder="Specify a dedication or leave a note for our team" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-xl text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 rounded-full bg-ring hover:bg-ring/90 text-primary text-lg font-semibold shadow-lg shadow-ring/20 transition-all hover:scale-[1.02]">
        {isSubmitting ? <span className="flex items-center gap-2">
            <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></span>
            Processing...
          </span> : <span className="flex items-center gap-2">
            Donate Now <Heart className="w-4 h-4 fill-current" />
          </span>}
      </Button>
      
      <p className="text-xs text-center text-muted-foreground mt-4">
        By donating, you agree to our <Link to="#" className="underline hover:text-primary">Terms of Service</Link> and <Link to="#" className="underline hover:text-primary">Privacy Policy</Link>.
      </p>
    </Form>;
}